/**
 * world_env.ts — the per-world ENVIRONMENT (Edit Mode panel) as the client
 * sees it: one mirrored row (`world_environment`) turned into multipliers and
 * tints the lighting / fog / ambience modules fold in on top of the global
 * sky-band + weather profiles. Pure helpers + a module-level current value
 * (set by WorldEnvModule on world entry / row change) so the render modules
 * read it without wiring; `atlas:environment-updated` makes them re-apply.
 */

export interface WorldEnv {
  timeMode: 'cycle' | 'fixed';
  fixedTimeOfDay: number;
  weatherMode: 'auto' | 'fixed';
  fogMul: number;
  fogColorHex: string;
  sunMul: number;
  ambientMul: number;
  sunTintHex: string;
  ambienceKey: string;
  ambienceGain: number;
}

export const DEFAULT_WORLD_ENV: WorldEnv = {
  timeMode: 'cycle', fixedTimeOfDay: 0.5, weatherMode: 'auto',
  fogMul: 1, fogColorHex: '', sunMul: 1, ambientMul: 1, sunTintHex: '',
  ambienceKey: '', ambienceGain: 1,
};

export const WEATHERS = ['clear', 'cloudy', 'rain', 'storm', 'fog', 'snow'] as const;
export const AMBIENCE_KEYS: Array<{ key: string; label: string }> = [
  { key: '', label: 'Biome default' },
  { key: 'ambient_town', label: 'Town' },
  { key: 'ambient_city', label: 'City' },
  { key: 'ambient_crystal_forest', label: 'Crystal forest' },
  { key: 'ambient_moss_wetland', label: 'Moss wetland' },
  { key: 'ambient_regolith_plains', label: 'Regolith plains' },
  { key: 'ambient_rocky_highlands', label: 'Rocky highlands' },
  { key: 'ambient_dune_desert', label: 'Dune desert' },
  { key: 'ambient_fungal_basin', label: 'Fungal basin' },
  { key: 'ambient_underwater', label: 'Underwater' },
  { key: 'ambient_space', label: 'Space' },
];
export const DAY_LENGTHS: Array<{ label: string; seconds: number }> = [
  { label: '10 min', seconds: 600 }, { label: '30 min', seconds: 1800 }, { label: '1 h', seconds: 3600 },
  { label: '2 h (default)', seconds: 7200 }, { label: '6 h', seconds: 21_600 }, { label: '24 h (real time)', seconds: 86_400 },
];

let current: WorldEnv = { ...DEFAULT_WORLD_ENV };

/** Install the current world's environment (null/undefined = defaults). Returns true when something changed. */
export function setWorldEnv(row: Partial<WorldEnv> | null | undefined): boolean {
  const next: WorldEnv = { ...DEFAULT_WORLD_ENV };
  if (row) {
    if (row.timeMode === 'fixed' || row.timeMode === 'cycle') next.timeMode = row.timeMode;
    if (row.weatherMode === 'fixed' || row.weatherMode === 'auto') next.weatherMode = row.weatherMode;
    for (const k of ['fixedTimeOfDay', 'fogMul', 'sunMul', 'ambientMul', 'ambienceGain'] as const) {
      const v = row[k];
      if (typeof v === 'number' && Number.isFinite(v)) next[k] = v;
    }
    for (const k of ['fogColorHex', 'sunTintHex', 'ambienceKey'] as const) {
      const v = row[k];
      if (typeof v === 'string') next[k] = v;
    }
  }
  const changed = JSON.stringify(next) !== JSON.stringify(current);
  current = next;
  return changed;
}

export function worldEnv(): WorldEnv { return current; }

/** Sun/ambient intensities after the world's multipliers. */
export function envLight(sun: number, ambient: number, env: WorldEnv = current): { sun: number; ambient: number } {
  return { sun: sun * env.sunMul, ambient: ambient * env.ambientMul };
}

/** Fog density after the world's multiplier. */
export function envFogDensity(density: number, env: WorldEnv = current): number { return density * env.fogMul; }

/** How strongly an authored tint blends into the computed color (0 = none). */
export const TINT_BLEND = 0.55;

/** "#rrggbb" → [r,g,b] 0..1 (null when malformed). */
export function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** 0..1 time of day → "HH:MM". */
export function todToClock(tod: number): string {
  const mins = Math.round(((tod % 1) + 1) % 1 * 24 * 60);
  const h = Math.floor(mins / 60) % 24, m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** "HH:MM" (or hours as a number) → 0..1. */
export function clockToTod(text: string): number | null {
  const m = /^\s*(\d{1,2})(?::(\d{1,2}))?\s*$/.exec(text);
  if (!m) return null;
  const h = Number(m[1]), mi = Number(m[2] ?? 0);
  if (h > 24 || mi > 59) return null;
  return ((h * 60 + mi) / (24 * 60)) % 1;
}
